/*
 *
 * Copyright (c) 2025 Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Alexander Orlov ("CONFIDENTIAL INFORMATION").
 *
 * Use, reproduction, modification, or distribution of this code
 * is permitted only pursuant to a written license expressly granted
 * by Alexander Orlov.
 *
 * You shall not disclose such confidential information and shall use
 * it only in accordance with the terms of the written license agreement
 * you have entered into with Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft
 *
 */


import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { OwnerDTO, CarType, CarInsurance } from '@/types';
import type { GuiSettings } from '@/api/ownerSettings';

interface AppState {
  // Loading state
  isLoading: boolean;
  setLoading: (loading: boolean) => void;

  // Selected owner (for admin)
  selectedOwner: OwnerDTO | null;
  setSelectedOwner: (owner: OwnerDTO | null) => void;

  // GUI settings for the logged-in user (controls page visibility)
  guiSettings: GuiSettings | null;
  setGuiSettings: (gui: GuiSettings | null) => void;

  // Cached data
  carTypes: CarType[];
  setCarTypes: (types: CarType[]) => void;

  carBrands: string[];
  setCarBrands: (brands: string[]) => void;

  insurances: CarInsurance[];
  setInsurances: (insurances: CarInsurance[]) => void;

  fullCoverageInsurances: CarInsurance[];
  setFullCoverageInsurances: (insurances: CarInsurance[]) => void;

  // Owners list (for admin)
  owners: OwnerDTO[];
  setOwners: (owners: OwnerDTO[]) => void;

  // Reset all state (used on logout)
  reset: () => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      // Loading state
      isLoading: false,
      setLoading: (loading: boolean) => set({ isLoading: loading }),

      // Selected owner
      selectedOwner: null,
      setSelectedOwner: (owner: OwnerDTO | null) => set({ selectedOwner: owner }),

      // GUI settings (logged-in user)
      guiSettings: null,
      setGuiSettings: (gui: GuiSettings | null) => set({ guiSettings: gui }),

      // Cached data
      carTypes: [],
      setCarTypes: (types: CarType[]) => set({ carTypes: types }),

      carBrands: [],
      setCarBrands: (brands: string[]) => set({ carBrands: brands }),

      insurances: [],
      setInsurances: (insurances: CarInsurance[]) => set({ insurances }),

      fullCoverageInsurances: [],
      setFullCoverageInsurances: (insurances: CarInsurance[]) =>
        set({ fullCoverageInsurances: insurances }),

      // Owners list
      owners: [],
      setOwners: (owners: OwnerDTO[]) => set({ owners }),

      // Reset all state (logout)
      reset: () => set({
        isLoading: false,
        selectedOwner: null,
        guiSettings: null,
        carTypes: [],
        carBrands: [],
        insurances: [],
        fullCoverageInsurances: [],
        owners: [],
      }),
    }),
    {
      name: 'aegis-app-storage',
      partialize: (state) => ({
        selectedOwner: state.selectedOwner,
        guiSettings: state.guiSettings,
      }),
    }
  )
);
